import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { Box, CircularProgress, Toolbar, Typography, styled } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runRestaurantClosedProcedure } from '../../../apis/runRestaurantClosedProcedure';
import { runRestaurantOpenProcedure } from '../../../apis/runRestaurantOpenProcedure';
import { traceAction } from '../../../libs/apiFetch';
import EDIT_ICON from '../../../assets/svg/edit-icon.svg';
import LOCATION_ICON from '../../../assets/svg/location-icon.svg';
import TABILIFE_LOGO_SVG from '../../../assets/svg/tabilife-logo.svg';
import Button from '../../../components/Button/Button';
import CustomSwitch from '../../../components/Switch/Switch';
import db_config from '../../../configs/db_config';
import { LANG_TEXT } from '../../../constants/language';
import { ROLES } from '../../../consts/language';
import { useAuthContext } from '../../../context/AuthContext/AuthContext';
import { NewOrderAlertsContext } from '../../../context/NewOrderAlerts';
import { useSettingsContext } from '../../../context/SettingsContext';
import usePrinter from '../../../hooks/usePrinter';
import { updateDoc } from '../../../libs/firestore-wrapper';
import { getCurrentSettings } from '../../../modals/order';
import CustomModal from '../../../pages/Dashboard/components/Modal/Modal';
import Notifications from './Notifications';
import { buttonPrintStyle, useStyles } from './styles';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

export const AppBarCustom = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: '100%',
    height: '72px',
    borderBottom: '1px solid var(--Gray-200, #EAECF0)',
    background: 'var(--Primary-50, #F2FBF3)',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function AppBar() {
  const classes = useStyles();

  const { currentSetting, setCurrentSetting } = useSettingsContext();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setValue(currentSetting.region);
  };
  const [switchValue, setSwitchValue] = useState<boolean>(false);
  const { userInfo } = useAuthContext();
  const [value, setValue] = useState<string>('');
  const { t } = useTranslation();
  const { notifications, markAsRead } = useContext(NewOrderAlertsContext);
  const [isLoading, setIsLoading] = useState(false);
  const { addToQueue } = usePrinter();
  const [isPrintProcessing, setIsPrintProcessing] = useState(false);
  const lastPrintClickRef = useRef<number>(0);
  const PRINT_CLICK_THROTTLE_MS = 1000;

  useEffect(() => {
    setSwitchValue((currentSetting as any)?.restaurant_open_state === db_config.RESTAURANT_OPEN_STATE_OPENED ? true : false || false);
    setValue(currentSetting.region);
  }, [currentSetting]);

  const handleSwitchChange = async (isChecked: boolean) => {
    setIsLoading(true);
    const correlationId = traceAction('restaurant_open_state_toggle', {
      restaurantId: userInfo.restaurant_id,
      target: isChecked ? 'open' : 'closed',
    });
    try {
      setSwitchValue(isChecked);
      if (isChecked) {
        await runRestaurantOpenProcedure(userInfo.restaurant_id, correlationId);
      } else {
        await runRestaurantClosedProcedure(userInfo.restaurant_id, correlationId);
      }
      setCurrentSetting((prevData: any) => ({
        ...prevData,
        restaurant_open_state: isChecked ? db_config.RESTAURANT_OPEN_STATE_OPENED : db_config.RESTAURANT_OPEN_STATE_PREPARING,
      }));
    } catch (error) {
      console.error('Error updating restaurant state:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRegion = (newValue: string) => {
    setValue(newValue);
    setCurrentSetting((prevData: any) => ({
      ...prevData,
      region: newValue,
    }));
    updateDoc(getCurrentSettings(userInfo.restaurant_id), { region: newValue });
    setOpenModal(false);
  };

  return (
    <AppBarCustom open={true} position="sticky">
      {isLoading && (
        <div className="loading-overlay">
          <CircularProgress />
        </div>
      )}
      <Toolbar className={classes.TollBarApp}>
        <img src={`${TABILIFE_LOGO_SVG}`} alt={'TABILIFE_LOGO_SVG'} loading="lazy" />
        {userInfo.role === ROLES.STAFF && (
          <Box className={classes.HeaderStyles}>
            <Box className={classes.BoxPrint}>
              <Button
                id="print_button"
                fullWidth={false}
                title={t(LANG_TEXT.PRINT)}
                buttonStyle={buttonPrintStyle}
                sxTextStyle={{ color: 'var(--Gray-700, #344054)', fontWeight: 600, lineHeight: '26px', fontSize: '16px', width: 'max-content' }}
                onClick={() => {
                  const now = Date.now();
                  if (lastPrintClickRef.current > 0 && now - lastPrintClickRef.current < PRINT_CLICK_THROTTLE_MS) {
                    return;
                  }
                  lastPrintClickRef.current = now;
                  setIsPrintProcessing(true);
                  try {
                    addToQueue({});
                  } finally {
                    setTimeout(() => setIsPrintProcessing(false), PRINT_CLICK_THROTTLE_MS);
                  }
                }}
                disable={isPrintProcessing}
                startIcon={<LocalPrintshopIcon sx={{ color: 'rgba(52, 64, 84, 1)' }} />}
              />
            </Box>
            <Box className={classes.BoxSettings}>
              <img src={`${LOCATION_ICON}`} alt={'LOCATION_ICON'} loading="lazy" />
              <Typography fontSize={16} lineHeight={'24px'} color={'var(--Gray-400, #98A2B3)'}>
                {t(LANG_TEXT.LOCATION)}:
              </Typography>
              <Box className={classes.LocationText}>
                <span style={{ color: switchValue ? 'var(--Primary-700, #296C32)' : 'var(--Gray-400, #98A2B3)' }}>{switchValue ? `${value}` : '--'}</span>
                {switchValue && (
                  <Box className={classes.ButtonLocation} onClick={handleOpenModal}>
                    <img src={EDIT_ICON} height={20} width={20} alt="EDIT_ICON" />
                  </Box>
                )}
                <CustomModal value={value} setValue={setValue} open={openModal} handleClose={handleCloseModal} handleSubmitRegion={handleSubmitRegion} />
              </Box>
            </Box>
            <Box className={classes.BoxSettings}>
              <CustomSwitch onChange={handleSwitchChange} isChecked={switchValue} />
              <Typography fontSize={16} lineHeight={'24px'} color={'var(--Gray-400, #98A2B3)'}>
                {t(LANG_TEXT.STATUS)}:{' '}
                <span style={{ color: switchValue ? 'var(--Primary-700, #296C32)' : 'var(--Gray-400, #98A2B3)' }}>
                  {switchValue ? `${t(LANG_TEXT.ONLINE)}` : `${t(LANG_TEXT.OFFLINE)}`}
                </span>
              </Typography>
            </Box>
            <Notifications notifications={notifications} markAsRead={markAsRead} />
          </Box>
        )}
        {/* {userInfo.role === ROLES.SUPER_ADMIN && !!restaurantId && <MenuSettings />} */}

        {/* {userInfo.role === ROLES.SUPER_ADMIN && (
          <Box className={classes.HeaderStyles}>
            <Typography fontSize={18} lineHeight={'28px'} fontWeight={600} color={'var(--Gray-600, #475467)'}>
              Restaurant Name
            </Typography>
          </Box>
        )} */}
      </Toolbar>
    </AppBarCustom>
  );
}
